import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { can } from "@/lib/permissions";
import { PageHeader, EmptyState, StatusPill } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Plus, PencilSimple, Trash, Power, Plugs, EnvelopeSimple, ChatText, ArrowSquareOut,
} from "@phosphor-icons/react";

const COPY = {
  sms: {
    title: "SMS Providers",
    subtitle: "Configure well-known SMS gateways (Twilio, Vonage, Telnyx, …) or add your own. Credentials are stored encrypted.",
    empty: "No SMS providers yet",
    menu: "providers-sms",
    testid: "sms-providers",
    Icon: ChatText,
  },
  email: {
    title: "Email Providers",
    subtitle: "Configure well-known email APIs (SendGrid, SES, Mailgun, …) or a generic SMTP relay. Credentials are stored encrypted.",
    empty: "No email providers yet",
    menu: "providers-email",
    testid: "email-providers",
    Icon: EnvelopeSimple,
  },
};

function blankFieldValues(fields) {
  const settings = {};
  const secrets = {};
  (fields || []).forEach((f) => {
    if (f.secret) secrets[f.key] = "";
    else settings[f.key] = f.value || "";
  });
  return { settings, secrets };
}

export default function ThirdPartyProviders({ kind }) {
  const { user } = useAuth();
  const copy = COPY[kind] || COPY.sms;
  const menuKey = copy.menu;
  const canAdd = can(user, menuKey, "add");
  const canEdit = can(user, menuKey, "edit");
  const canDelete = can(user, menuKey, "delete");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [customFields, setCustomFields] = useState([]);
  const [form, setForm] = useState({
    name: "", slug: "", description: "", website: "", status: "inactive",
    settings: {}, secrets: {},
  });
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/third-party-providers", {
        params: { kind, ...(q.trim() ? { q: q.trim() } : {}) },
      });
      setRows(data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [kind, q]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/third-party-providers/meta/fields", { params: { kind } })
      .then(({ data }) => setCustomFields(data.fields || []))
      .catch(() => setCustomFields([]));
  }, [kind]);

  const formFields = useMemo(() => {
    if (edit?.fields?.length) return edit.fields;
    return customFields;
  }, [edit, customFields]);

  const openCreate = () => {
    setEdit(null);
    const blanks = blankFieldValues(customFields);
    setForm({
      name: "", slug: "", description: "", website: "", status: "inactive",
      ...blanks,
    });
    setOpen(true);
  };

  const openEdit = (row) => {
    setEdit(row);
    const { settings, secrets } = blankFieldValues(row.fields);
    (row.fields || []).forEach((f) => {
      if (!f.secret) settings[f.key] = f.value || "";
    });
    setForm({
      name: row.name || "",
      slug: row.slug || "",
      description: row.description || "",
      website: row.website || "",
      status: row.status || "inactive",
      settings,
      secrets,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const settings = {};
      const secrets = {};
      formFields.forEach((f) => {
        if (f.secret) {
          const v = (form.secrets?.[f.key] || "").trim();
          if (v) secrets[f.key] = v;
        } else {
          settings[f.key] = (form.settings?.[f.key] || "").trim();
        }
      });
      if (edit) {
        await api.patch(`/third-party-providers/${edit.id}`, {
          name: form.name.trim(),
          description: form.description.trim() || null,
          website: form.website.trim() || null,
          status: form.status,
          settings,
          secrets,
        });
      } else {
        await api.post("/third-party-providers", {
          kind,
          name: form.name.trim(),
          slug: form.slug.trim() || undefined,
          description: form.description.trim() || null,
          website: form.website.trim() || null,
          status: form.status,
          settings,
          secrets,
        });
      }
      toast.success(edit ? "Provider saved" : "Provider added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const run = async (id, fn) => {
    setBusy(id);
    try {
      await fn();
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const toggleStatus = (row) => run(row.id, async () => {
    const status = row.status === "active" ? "inactive" : "active";
    await api.post(`/third-party-providers/${row.id}/status`, { status });
    toast.success(status === "active" ? "Enabled" : "Disabled");
  });

  const doDelete = async () => {
    if (!confirm) return;
    const row = confirm;
    setConfirm(null);
    await run(row.id, async () => {
      await api.delete(`/third-party-providers/${row.id}`);
      toast.success("Provider removed");
    });
  };

  const Icon = copy.Icon;

  return (
    <div data-testid={copy.testid}>
      <PageHeader
        title={copy.title}
        subtitle={copy.subtitle}
        actions={(
          <div className="flex items-center gap-2">
            <Input
              className="h-9 w-52 rounded-lg"
              placeholder="Search…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid={`${copy.testid}-search`}
            />
            {canAdd && (
              <Button className="rounded-lg gap-1.5" onClick={openCreate} data-testid={`${copy.testid}-add`}>
                <Plus className="w-4 h-4" /> Add provider
              </Button>
            )}
          </div>
        )}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Icon}
          title={copy.empty}
          description="Built-in providers load automatically. Use Add provider for a custom gateway."
          testid={`${copy.testid}-empty`}
        />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Provider</TableHead>
                <TableHead>Slug</TableHead>
                <TableHead>Credentials</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.id} data-testid={`${copy.testid}-row-${row.slug}`}>
                  <TableCell>
                    <div className="font-medium flex items-center gap-2">
                      <Plugs className="w-4 h-4 text-muted-foreground shrink-0" />
                      {row.name}
                      {row.is_builtin && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
                          Built-in
                        </span>
                      )}
                    </div>
                    {row.description && (
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{row.description}</p>
                    )}
                    {row.website && (
                      <a
                        href={row.website}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 text-[11px] text-primary mt-0.5 hover:underline"
                      >
                        Docs <ArrowSquareOut className="w-3 h-3" />
                      </a>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{row.slug}</TableCell>
                  <TableCell className="text-sm">
                    {row.has_credentials ? (
                      <span className="text-emerald-600">Configured</span>
                    ) : (
                      <span className="text-muted-foreground">Not set</span>
                    )}
                  </TableCell>
                  <TableCell><StatusPill status={row.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2"
                          disabled={busy === row.id}
                          onClick={() => toggleStatus(row)}
                          title={row.status === "active" ? "Disable" : "Enable"}
                          data-testid={`${copy.testid}-toggle-${row.slug}`}
                        >
                          <Power className="w-4 h-4" />
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2"
                          onClick={() => openEdit(row)}
                          title="Configure"
                          data-testid={`${copy.testid}-edit-${row.slug}`}
                        >
                          <PencilSimple className="w-4 h-4" />
                        </Button>
                      )}
                      {canDelete && !row.is_builtin && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-8 px-2 text-destructive hover:text-destructive"
                          disabled={busy === row.id}
                          onClick={() => setConfirm(row)}
                          title="Delete"
                          data-testid={`${copy.testid}-delete-${row.slug}`}
                        >
                          <Trash className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`${copy.testid}-dialog`}>
          <DialogHeader>
            <DialogTitle>{edit ? `Configure ${edit.name}` : `Add ${kind === "sms" ? "SMS" : "email"} provider`}</DialogTitle>
            <DialogDescription>
              {edit?.is_builtin
                ? "Enter credentials for this built-in provider. Leave secret fields blank to keep the existing value."
                : "Custom providers use a generic credential form. Built-in providers cannot be deleted — disable them instead."}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-1">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                className="rounded-lg"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                disabled={!!edit?.is_builtin}
                data-testid={`${copy.testid}-name`}
              />
            </div>
            {!edit && (
              <div className="space-y-1.5">
                <Label>Slug <span className="text-muted-foreground font-normal">(optional)</span></Label>
                <Input
                  className="rounded-lg font-mono text-sm"
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  placeholder="auto-from-name"
                  data-testid={`${copy.testid}-slug`}
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Textarea
                className="rounded-lg min-h-[60px]"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid={`${copy.testid}-description`}
              />
            </div>
            {!edit?.is_builtin && (
              <div className="space-y-1.5">
                <Label>Website</Label>
                <Input
                  className="rounded-lg"
                  value={form.website}
                  onChange={(e) => setForm({ ...form, website: e.target.value })}
                  placeholder="https://"
                />
              </div>
            )}

            <div className="border-t border-border pt-3 space-y-3">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Credentials & settings</p>
              {formFields.map((f) => (
                <div key={f.key} className="space-y-1.5">
                  <Label>
                    {f.label}
                    {f.secret && f.mask && (
                      <span className="ml-2 text-muted-foreground font-normal">({f.mask})</span>
                    )}
                  </Label>
                  <Input
                    className="rounded-lg"
                    type={f.secret ? "password" : "text"}
                    autoComplete="off"
                    placeholder={f.secret ? (f.configured ? "Leave blank to keep current" : (f.placeholder || "")) : (f.placeholder || "")}
                    value={f.secret ? (form.secrets?.[f.key] || "") : (form.settings?.[f.key] || "")}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (f.secret) {
                        setForm({ ...form, secrets: { ...form.secrets, [f.key]: v } });
                      } else {
                        setForm({ ...form, settings: { ...form.settings, [f.key]: v } });
                      }
                    }}
                    data-testid={`${copy.testid}-field-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-lg" disabled={saving} onClick={save} data-testid={`${copy.testid}-save`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirm} onOpenChange={(v) => !v && setConfirm(null)}>
        <DialogContent data-testid={`${copy.testid}-delete-dialog`}>
          <DialogHeader>
            <DialogTitle>Delete {confirm?.name}?</DialogTitle>
            <DialogDescription>Removes this custom provider and its stored credentials. Built-in providers cannot be deleted.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" className="rounded-lg" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button
              className="rounded-lg bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={doDelete}
              data-testid={`${copy.testid}-delete-confirm`}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
